import React, {
  useCallback, useId, useMemo, useState,
} from 'react';

import Icon from 'components/DesignSystem/Icon';
import OptionsPopover, { OptionsPopoverStatus } from 'components/Flux/Personalized/MyStudies/FilterBar/OptionsPopover';
import { CompanyScope } from 'helper/products/fileViewer';
import {
  buildCompanyOptions,
  companyLabel,
  companySearchPlaceholder,
} from 'helper/products/fileViewerCompanies';
import { useListFileViewerCompaniesQuery } from 'services/api/fileViewer';
import useStudiesErrorState, { StudiesErrorKind } from 'utils/hooks/flux/personalized/useStudiesErrorState';

import './index.scss';

/** Texto do botão enquanto o admin não escolheu (estado "Escolha uma empresa"). */
export const CHOOSE_COMPANY_LABEL = 'Escolha uma empresa';

/** Falha ao carregar as empresas, por tipo (`useStudiesErrorState`, modo `auxiliary`). */
const ERROR_MESSAGES: Readonly<Record<StudiesErrorKind, string>> = {
  network: 'Não foi possível carregar as empresas. Verifique sua conexão.',
  server: 'Não foi possível carregar as empresas. Tente de novo em instantes.',
  forbidden: 'Você não tem acesso à lista de empresas.',
};

/** Usuário logado de verdade (`selectUserId` devolve -1 sem JWT). */
const isValidUserId = (userId: number): boolean => Number.isSafeInteger(userId) && userId > 0;

export interface CompanySelectorProps {
  /** Escopo em vigor; `null` enquanto o admin não escolheu. */
  value: CompanyScope | null;
  /** Usuário logado (`selectUserId`): sem usuário válido (sem JWT), a lista não é pedida. */
  viewerUserId: number;
  /** Recebe o escopo escolhido ("todas" ou `{ companyId }`); quem chama grava URL e sessão. */
  onChange: (next: CompanyScope) => void;
}

/**
 * Seletor de empresa da tela Meus Arquivos, só para admin: a tela nem o monta para cliente, e a
 * lista de empresas só é pedida com ele montado. Cópia do `CompanyFilter` de Meus Estudos (Flux)
 * sobre o mesmo `OptionsPopover`, com as regras de `helper/products/fileViewerCompanies`.
 *
 * - Botão: "Escolha uma empresa" (sem escolha), "Todas as empresas" ou o nome da empresa (o id
 *   enquanto a lista não chega); com empresa escolhida, a borda azul de filtro ativo.
 * - Lista: "Todas as empresas" fixo no topo, depois cada empresa por nome, com o apelido embaixo
 *   e "N pastas" à direita; Ampere (id 1) é uma empresa como as outras. Busca no nome e no apelido.
 *   Carregando ou com erro, "Todas as empresas" continua escolhível, junto da mensagem.
 * - Erros: `useStudiesErrorState(error, { mode: 'auxiliary' })`. Rede, servidor e 403 que não é
 *   de sessão ficam na lista, como mensagem; a tela segue. Durante o "Tentar de novo"
 *   (`isFetching`), a mensagem volta a ser a de carregando.
 *
 * A lista é pedida ao montar (e não ao abrir): o placeholder precisa do total e o botão, do nome
 * da empresa vinda da URL. É uma requisição leve, e o cache do RTK Query a reaproveita.
 */
export default function CompanySelector({ value, viewerUserId, onChange }: CompanySelectorProps) {
  const nameId = useId();
  const toggleId = useId();
  const [open, setOpen] = useState(false);
  const {
    currentData, error, isFetching, refetch,
  } = useListFileViewerCompaniesQuery(undefined, { skip: !isValidUserId(viewerUserId) });
  const errorKind = useStudiesErrorState(error, { mode: 'auxiliary' });

  // Sem a resposta (carregando ou erro), só "Todas as empresas"
  const options = useMemo(() => buildCompanyOptions(currentData), [currentData]);
  let selectedKey: string | null = null;
  if (value === 'todas') selectedKey = 'todas';
  else if (value !== null) selectedKey = String(value.companyId);
  // O pedido do "Tentar de novo" não limpa o `error` (RTK Query 1.9): `isFetching` vence
  let status: OptionsPopoverStatus = 'loading';
  if (errorKind && !isFetching) status = 'error';
  else if (currentData) status = 'ready';

  const handleSelect = useCallback((key: string) => {
    const chosen = options.find((option) => option.key === key);
    if (chosen) onChange(chosen.value);
  }, [options, onChange]);

  const retry = useCallback(() => {
    refetch();
  }, [refetch]);

  const label = value === null ? CHOOSE_COMPANY_LABEL : companyLabel(value, currentData);
  const isFiltered = value !== null && value !== 'todas';

  return (
    <div className="file-explorer-company">
      <span className="file-explorer-company-label">
        <span id={nameId}>EMPRESA</span>
        {' '}
        <span className="file-explorer-company-note">· somente admin</span>
      </span>
      <OptionsPopover
        open={open}
        onOpenChange={setOpen}
        toggleId={toggleId}
        // Nome "EMPRESA <empresa>", como o filtro de Meus Estudos
        toggleAriaLabelledBy={`${nameId} ${toggleId}`}
        toggleClassName={`file-explorer-company-toggle${isFiltered ? ' is-filtered' : ''}`}
        toggleContent={(
          <>
            <span className="file-explorer-company-toggle-label">{label}</span>
            <span className="file-explorer-company-toggle-chevron" aria-hidden="true">
              <Icon iconType="down_arrow" color="currentColor" size="small" />
            </span>
          </>
        )}
        menuClassName="file-explorer-company-menu"
        searchLabel="Buscar empresa"
        searchPlaceholder={companySearchPlaceholder(currentData)}
        listLabel="Empresas"
        options={options}
        selectedKey={selectedKey}
        onSelect={handleSelect}
        status={status}
        loadingMessage="Carregando empresas…"
        emptyMessage="Nenhuma empresa encontrada."
        errorMessage={errorKind ? ERROR_MESSAGES[errorKind] : undefined}
        onRetry={errorKind === 'forbidden' ? undefined : retry}
      />
    </div>
  );
}
